import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const DIRECT_API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch user data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserData();
    } else {
      // Clear data when not authenticated
      setDocuments([]);
      setChatSessions([]);
      setCurrentSession(null);
      setSubscription(null);
      setUsage(null);
    }
  }, [isAuthenticated]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const [subRes, usageRes, docsRes, chatsRes] = await Promise.allSettled([
        axios.get('/api/subscription/status'),
        axios.get('/api/subscription/usage'),
        axios.get('/api/documents'),
        axios.get('/api/chat/sessions')
      ]);

      // Defaults
      setDocuments([]);
      setChatSessions([]);

      if (subRes.status === 'fulfilled') {
        setSubscription(subRes.value.data);
      }

      if (usageRes.status === 'fulfilled') {
        setUsage(usageRes.value.data);
      }

      if (docsRes.status === 'fulfilled') {
        const docs = docsRes.value?.data?.documents || [];
        setDocuments(docs);
      }

      if (chatsRes.status === 'fulfilled') {
        const sessions = chatsRes.value?.data?.sessions || [];
        setChatSessions(sessions);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Set defaults on error
      setDocuments([]);
      setChatSessions([]);
      setSubscription(null);
      setUsage(null);
    } finally {
      setLoading(false);
    }
  };

  const uploadDocument = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadUrl = DIRECT_API_BASE ? `${DIRECT_API_BASE}/upload` : '/api/upload';
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Add new document to the list
      setDocuments(prev => [response.data, ...prev]);
      
      // Update usage
      if (usage) {
        setUsage(prev => ({
          ...prev,
          documents_used: prev.documents_used + 1
        }));
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      await axios.delete(`/api/documents/${documentId}`);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      // Update usage
      if (usage) {
        setUsage(prev => ({
          ...prev,
          documents_used: Math.max(0, prev.documents_used - 1)
        }));
      }
    } catch (error) {
      throw error;
    }
  };

  const renameDocument = async (documentId, filename) => {
    try {
      await axios.put(`/api/documents/${documentId}/rename`, { filename });
      setDocuments(prev => prev.map(doc => doc.id === documentId ? { ...doc, filename } : doc));
    } catch (error) {
      throw error;
    }
  };

  const createChatSession = async (title) => {
    try {
      const response = await axios.post('/api/chat/sessions', { title });
      const newSession = response.data;
      setChatSessions(prev => [newSession, ...prev]);
      setCurrentSession(newSession);
      return newSession;
    } catch (error) {
      throw error;
    }
  };

  const sendMessage = async (sessionId, message) => {
    try {
      const response = await axios.post(`/api/chat/sessions/${sessionId}/messages`, {
        message
      });
      
      // Update usage
      if (usage) {
        setUsage(prev => ({
          ...prev,
          chat_sessions_used: prev.chat_sessions_used + 1
        }));
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const searchDocuments = async (query) => {
    try {
      const response = await axios.post('/api/search', { query });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const queryDocuments = async (query) => {
    try {
      const response = await axios.post('/api/query', { query });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const upgradePlan = async (planType) => {
    try {
      const response = await axios.post('/api/billing/checkout-session', {
        plan_type: planType
      });

      const checkoutUrl = response?.data?.checkout_url;
      if (!checkoutUrl) {
        throw new Error('No checkout URL received');
      }

      window.location.href = checkoutUrl;
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const manageBilling = async () => {
    try {
      const response = await axios.post('/api/billing/portal-session');
      const portalUrl = response?.data?.portal_url;
      if (!portalUrl) throw new Error('No billing portal URL received');
      window.location.href = portalUrl;
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getChatSession = async (sessionId) => {
    try {
      const response = await axios.get(`/api/chat/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    documents,
    chatSessions,
    currentSession,
    subscription,
    usage,
    loading,
    setCurrentSession,
    fetchUserData,
    uploadDocument,
    deleteDocument,
    renameDocument,
    createChatSession,
    sendMessage,
    searchDocuments,
    queryDocuments,
    upgradePlan,
    manageBilling,
    getChatSession
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
